import { Component, DestroyRef, ElementRef, EventEmitter, forwardRef, HostListener, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { ReviewerService } from '../../services/reviewer.service';
import { AppConfigService } from '../../services/app-config.service';
import { PeoplePickerCacheService } from '../../services/people-picker-cache.service';

export interface IPeopleDetails {
  EnterpriseId: string;
  InternetMail: string;
  FirstName?: string;
  LastName?: string;
  EmploymentStatusCode?: string;
}

@Component({
  selector: 'app-people-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './people-picker.component.html',
  styleUrl: './people-picker.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PeoplePickerComponent),
      multi: true,
    },
  ],
})
export class PeoplePickerComponent implements ControlValueAccessor, OnInit {
  @Input() placeholder = '';
  @Input() disabled = false;
  @Output() personSelected = new EventEmitter<IPeopleDetails>();

  searchText = '';
  peopleData: IPeopleDetails[] = [];
  dropdownOpen = false;


  private destroyRef = inject(DestroyRef);
  private sessionToken = '';
  private peopleXAPIKey = '';
  private tokenLoaded = false;
  private searchSubject = new Subject<string>();
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private hostRef: ElementRef<HTMLElement>,
    private reviewerService: ReviewerService,
    private appConfigService: AppConfigService,
    private cacheService: PeoplePickerCacheService,
  ) {}

  ngOnInit(): void {
    this.loadToken();
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      switchMap((text) => {
        if (text.length <= 2) {
          this.peopleData = [];
          this.dropdownOpen = false;
          return of([] as IPeopleDetails[]);
        }
        const peoplePickerAddr = (this.appConfigService.config['PeoplePickerAddress'] as string) || '';
        if (!peoplePickerAddr) return of([] as IPeopleDetails[]);
        return this.cacheService.search(peoplePickerAddr, text, this.peopleXAPIKey, this.sessionToken);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((items) => {
      this.peopleData = items;
      this.dropdownOpen = items.length > 0;
    });
  }

  writeValue(value: string | null): void {
    this.searchText = value ?? '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(value: string): void {
    this.searchText = value;
    this.onChange(value);
    this.searchSubject.next((value ?? '').trim());
  }

  onFocus(): void {
    if (!this.searchText) {
      this.peopleData = [];
    } else if (this.peopleData.length > 0) {
      this.dropdownOpen = true;
    }
  }

  onBlur(): void {
    this.onTouched();
  }

  selectPerson(person: IPeopleDetails): void {
    this.searchText = person.InternetMail;
    this.onChange(person.InternetMail);
    this.personSelected.emit(person);
    this.dropdownOpen = false;
    this.peopleData = [];
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.hostRef.nativeElement.contains(event.target as Node)) {
      this.dropdownOpen = false;
    }
  }

  private loadToken(): void {
    if (this.tokenLoaded) return;
    this.reviewerService.GetTokenforPeople()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.sessionToken = res?.access_token ?? '';
          this.peopleXAPIKey = res?.peopleXAPIKey ?? '';
          this.tokenLoaded = !!this.sessionToken;
        },
        error: (err) => console.error('PeoplePicker token error:', err),
      });
  }

}
